import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import update from 'immutability-helper'
import moment from 'moment'
import { LocaleState } from '@merchant-portal/merchant-portal-base'
import StatisticsListUI from './UI/StatisticsListUI'
import {
  getChart,
  getTop,
  getTrend,
  getStatus,
} from '../../../store/actions/statistics-actions'
import { ChainTypes } from '../../../models/chainTypes'
import { StatisticsScopes } from '../../../models/constants'
import { prettyPrintPeriod } from '../../../util/dateUtils'

const dateFormat = 'YYYY-MM-DD'
const privilegedUsers = [
  'STORE_MANAGER',
  'FINANCE',
  'SUPER_USER',
  'RESURS_ADMIN',
]

function getPeriodDates(period, timespan, format = dateFormat) {
  const momentTimespanUnits = {
    YEAR: 'year',
    MONTH: 'month',
    WEEK: 'isoweek',
  }
  const timeUnit = momentTimespanUnits[timespan] || momentTimespanUnits.YEAR
  const currentPeriod = moment.min(period, moment())
  return {
    start: currentPeriod.clone().startOf(timeUnit).format(format),
    end: moment
      .min(moment(), currentPeriod.clone().endOf(timeUnit))
      .format(format),
  }
}

class StatisticsList extends Component {
  constructor(props) {
    super(props)
    const { start, end } = getPeriodDates(moment(), 'MONTH')
    const scope = StatisticsScopes.STORE
    this.state = {
      query: {
        start,
        end,
        scope,
        user: this.props.auth.userId,
        period: 'MONTH',
        userId: null,
        filters: [],
        customStart: false,
      },
      timespan: 'MONTH',
    }
  }

  UNSAFE_componentWillMount() { //eslint-disable-line
    this.getStatistics()
  }

  onSetScope(scope) {
    this.setState(
      state =>
        update(state, {
          query: { scope: { $set: scope } },
        }),
      () => this.getStatistics(),
    )
  }

  onSetPeriod(timespan) {
    const { start, end } = getPeriodDates(moment(), timespan)
    this.setState(
      state =>
        update(state, {
          query: {
            start: { $set: start },
            end: { $set: end },
            period: { $set: timespan },
            customStart: { $set: false },
          },
          timespan: { $set: timespan },
        }),
      () => this.getStatistics(),
    )
  }

  onSelectDate(endDate, startDate) {
    const { start } = getPeriodDates(
      moment(endDate, dateFormat),
      this.state.timespan,
    )
    this.setState(
      state =>
        update(state, {
          query: {
            start: { $set: startDate || start },
            end: { $set: endDate },
            customStart: { $set: startDate && startDate !== start },
          },
        }),
      () => {
        if (moment(startDate).isValid() && moment(endDate).isValid()) {
          this.getStatistics()
        }
      },
    )
  }

  onSetFilters(filters) {
    this.setState(
      state => {
        const existingFilters = state.query.filters || []
        const newFilters = filters.filter(
          filter =>
            !existingFilters.some(
              existingFilter => existingFilter.value === filter.value,
            ),
        )

        return update(state, {
          query: {
            filters: { $set: [...existingFilters, ...newFilters] },
          },
        })
      },
      () => this.getStatistics(),
    )
  }

  onRemoveFilter(filters) {
    this.setState(
      state => {
        const existingFilters = state.query.filters || []
        const updatedFilters = existingFilters.filter(existingFilter =>
          filters.some(filter => filter.value === existingFilter.value),
        )

        return update(state, {
          query: {
            filters: { $set: updatedFilters },
          },
        })
      },
      () => this.getStatistics(),
    )
  }

  getStatistics() {
    this.props.getStatus(this.state.query)
    this.props.getTop(this.state.query)
    this.props.getChart(this.state.query, 'paymentmethods')

    if (privilegedUsers.includes(this.props.auth.role)) {
      this.props.getChart(this.state.query, 'gender')
      this.props.getChart(this.state.query, 'channels')
      this.props.getChart(this.state.query, 'returning')
    }
  }

  getTrend(userId) {
    this.props.getTrend({ ...this.state.query, userId })
  }

  render() {
    return (
      <LocaleState.Consumer>
        {locale => (
          <StatisticsListUI
            setScope={scope => this.onSetScope(scope)}
            setPeriod={start => this.onSetPeriod(start)}
            selectDate={(endDate, startDate) =>
              this.onSelectDate(endDate, startDate)
            }
            selectedDate={this.state.query.end}
            startDate={this.state.query.start}
            endDate={this.state.query.end}
            data={this.props.data}
            auth={this.props.auth}
            timespan={this.state.timespan}
            scope={this.state.query.scope}
            setTrend={userId => this.getTrend(userId)}
            periodType={this.state.query.period}
            period={prettyPrintPeriod(
              this.state.query.start,
              this.state.query.end,
              locale.language,
            )}
            privilegedUser={privilegedUsers.includes(this.props.auth.role)}
            filters={this.state.query.filters}
            setFilters={filters => this.onSetFilters(filters)}
            customStart={this.state.query.customStart}
            chainType={this.props.chainType}
            language={locale.language}
            onRemoveFilter={filters => this.onRemoveFilter(filters)}
          />
        )}
      </LocaleState.Consumer>
    )
  }
}

const mapStateToProps = state => {
  return {
    data: state.statistics,
    auth: state.auth,
    chainType:
      state.storeInfo.currentRepresentative &&
      state.storeInfo.currentRepresentative.chainType,
  }
}

const mapDispatchToProps = dispatch => ({
  getChart: (query, target) => {
    dispatch(getChart({ ...query, target }))
  },
  getTop: query => {
    dispatch(getTop(query))
  },
  getTrend: query => {
    dispatch(getTrend(query))
  },
  getStatus: query => {
    dispatch(getStatus(query))
  },
})

StatisticsList.propTypes = {
  getChart: PropTypes.func.isRequired,
  getTop: PropTypes.func.isRequired,
  getTrend: PropTypes.func.isRequired,
  getStatus: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  chainType: PropTypes.string,
}

StatisticsList.defaultProps = {
  chainType: ChainTypes.SINGLE_OWNER,
}

export default connect(mapStateToProps, mapDispatchToProps)(StatisticsList)
